import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { Button, FormControl, MenuItem, Select, Stack } from "@mui/material";

import { api, uuidv4 } from "../../../../../utils";
import ImageGroupComponent from "./ImageGroupComponent";
import { IError } from "../../../../../models/ReportPageModels";

interface GetImageGroupingCompaniesResponse {
    data: GetImageGroupingCompaniesData;
    error: IError;
}
interface GetImageGroupingCompaniesData {
    companies: string[];
}

interface GetImageGroupingResponse {
    data: GetImageGroupingData;
    error: IError;
}
interface GetImageGroupingData {
    imageGroups: ImageGroupingModel;
}
interface ImageGroupingModel {
    desktop: ImageNameToGroup[];
    mobile: ImageNameToGroup[];
}
export interface ImageNameToGroup {
    groupName: string;
    imageSubnames: string[];
}

interface SaveImageGroupingResponse {
    data: SaveImageGroupingData;
    error: IError;
}
interface SaveImageGroupingData {
    status: string;
}

interface SaveImageGroupingRequest {
    companyName: string;
    desktop: ImageNameToGroup[];
    mobile: ImageNameToGroup[];
}

function ReportImageGrouping() {
    const { enqueueSnackbar } = useSnackbar();

    const [companies, setCompanies] = useState<string[]>([]);

    const [selectedCompany, setSelectedCompany] = useState<string>("");

    const [desktopGroups, setDesktopGroups] = useState<ImageNameToGroup[]>([]);
    const [mobileGroups, setMobileGroups] = useState<ImageNameToGroup[]>([]);

    useEffect(() => {
        GetCompanies();
    }, []);

    const GetCompanies = async () => {
        try {
            const response: GetImageGroupingCompaniesResponse = await api.get("api/Admin/GetImageGroupingCompanies", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCompanies(response.data.companies)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    function CompanySelected(event: any) {
        var value = event.target.value;
        setSelectedCompany(value);

        LoadGroupStructure(value);
    }

    const LoadGroupStructure = async (companyName: string) => {
        setDesktopGroups([]);
        setMobileGroups([]);
        try {
            const response: GetImageGroupingResponse = await api.get(`api/Admin/GetImageGrouping?company=${companyName}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setDesktopGroups(response.data.imageGroups.desktop);
                setMobileGroups(response.data.imageGroups.mobile);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    function AddDesktop() {
        setDesktopGroups([...desktopGroups, { groupName: "", imageSubnames: [] }]);
    }

    function DeleteDesktopItem(index: number) {
        desktopGroups.splice(index, 1);
        setDesktopGroups([...desktopGroups]);
    }

    function AddMobile() {
        setMobileGroups([...mobileGroups, { groupName: "", imageSubnames: [] }]);
    }

    function DeleteMobileItem(index: number) {
        mobileGroups.splice(index, 1);
        setMobileGroups([...mobileGroups]);
    }

    const Save = async () => {
        var body: SaveImageGroupingRequest = {
            companyName: selectedCompany,
            desktop: desktopGroups,
            mobile: mobileGroups
        }

        try {
            const response: SaveImageGroupingResponse = await api.post(`api/Admin/SaveImageGrouping`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                },
                json: body
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Saved!", { variant: "success" });
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    return (
        <Stack>
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '150px' }}>Company name: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={selectedCompany}
                        onChange={CompanySelected}
                        size="small"
                        defaultValue={""}>
                        {companies.map((company, index) =>
                            <MenuItem
                                key={index}
                                value={company}>
                                {company}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>

                <Button
                    variant="outlined"
                    onClick={Save}
                    sx={{ width: "40px", height: "40px", marginLeft: "10px" }}>
                    Save
                </Button>
            </Stack>

            {/* Desktop */}
            <Stack sx={{ border: "1px solid black", padding: "5px" }}>
                <h2>Desktop</h2>
                {desktopGroups.map((group, index) =>
                    <ImageGroupComponent
                        key={uuidv4()}
                        group={group}
                        deleteGroupItem={() => DeleteDesktopItem(index)} />
                )}

                <Button
                    variant="outlined"
                    onClick={AddDesktop}
                    sx={{ width: "180px" }}>
                    Add desktop group
                </Button>
            </Stack>

            {/* Mobile */}
            <Stack sx={{ border: "1px solid black", padding: "5px", marginTop: "10px" }}>
                <h2>Mobile</h2>
                {mobileGroups.map((group, index) =>
                    <ImageGroupComponent
                        key={uuidv4()}
                        group={group}
                        deleteGroupItem={() => DeleteMobileItem(index)} />
                )}
                <Button
                    variant="outlined"
                    onClick={AddMobile}
                    sx={{ width: "170px" }}>
                    Add mobile group
                </Button>
            </Stack>
        </Stack>
    )
}

export default ReportImageGrouping;